import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { notifyShell, reportPendingCount } from '@harleys/shell-client';
import { getJobs, getProfile, getTemplates, submitSwms } from './services/api';
import type { AscoraJobSummary, M365Profile, RiskCode, SwmsStep, SwmsSubmission, SwmsTemplate } from './types';
import './styles/main.css';

type Screen = 'jobs' | 'form' | 'done';

const LOADING_QUIPS = [
  'Putting the hazards in alphabetical order. Briefly.',
  'Checking the controls know who is in charge.',
  'Giving the SWMS a very serious clipboard.',
  'Making the risk matrix feel seen.',
];

const LOADER_VARIANTS = ['stamp', 'drift', 'scan', 'dash', 'celebrate'] as const;

function pickLoadingQuip(): string {
  return LOADING_QUIPS[Math.floor(Math.random() * LOADING_QUIPS.length)];
}

function pickLoaderVariant(): (typeof LOADER_VARIANTS)[number] {
  return LOADER_VARIANTS[Math.floor(Math.random() * LOADER_VARIANTS.length)];
}

const TODAY = new Date();

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function addYear(date: Date): Date {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + 1);
  return next;
}

function riskLabel(risk: RiskCode): string {
  return { L: 'Low', M: 'Medium', H: 'High', EH: 'Extremely High' }[risk];
}

function App() {
  const [screen, setScreen] = useState<Screen>('jobs');
  const [jobs, setJobs] = useState<AscoraJobSummary[]>([]);
  const [templates, setTemplates] = useState<SwmsTemplate[]>([]);
  const [profile, setProfile] = useState<M365Profile | null>(null);
  const [selectedJob, setSelectedJob] = useState<AscoraJobSummary | null>(null);
  const [doneRef, setDoneRef] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingQuip] = useState(pickLoadingQuip);
  const [loadingVariant] = useState(pickLoaderVariant);

  useEffect(() => {
    (async () => {
      try {
        const [loadedJobs, loadedTemplates, loadedProfile] = await Promise.all([
          getJobs(),
          getTemplates(),
          getProfile().catch(() => null),
        ]);
        setJobs(loadedJobs);
        setTemplates(loadedTemplates);
        setProfile(loadedProfile);
        reportPendingCount('swms', loadedJobs.filter(job => job.swmsStatus !== 'Completed').length);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load SWMS');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="app">
        <div className="loading">
          <div className={`pipeline-loader pipeline-loader--${loadingVariant}`} aria-hidden="true">
            <div className="pipeline-loader__belt">
              <span className="pipeline-loader__card pipeline-loader__card--one" />
              <span className="pipeline-loader__card pipeline-loader__card--two" />
              <span className="pipeline-loader__dot" />
            </div>
            <div className="pipeline-loader__tick" />
          </div>
          <div className="loading-title">Loading SWMS jobs...</div>
          <div className="loading-quip">{loadingQuip}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="app"><div className="error"><strong>Couldn't load SWMS</strong><div>{error}</div></div></div>;
  }

  return (
    <div className="app">
      {screen === 'jobs' && (
        <JobsScreen
          jobs={jobs}
          onSelect={job => {
            setSelectedJob(job);
            setScreen('form');
          }}
        />
      )}
      {screen === 'form' && selectedJob && (
        <SwmsForm
          job={selectedJob}
          templates={templates}
          profile={profile}
          onCancel={() => setScreen('jobs')}
          onSubmitted={record => {
            notifyShell('success', `SWMS completed for ${selectedJob.jobNumber}`);
            setDoneRef(record);
            setJobs(current => current.map(job => job.jobNumber === selectedJob.jobNumber
              ? { ...job, swmsStatus: 'Completed', swmsReference: record }
              : job));
            setScreen('done');
          }}
        />
      )}
      {screen === 'done' && selectedJob && (
        <DoneScreen
          job={selectedJob}
          reference={doneRef}
          onBack={() => setScreen('jobs')}
        />
      )}
    </div>
  );
}

function JobsScreen({ jobs, onSelect }: { jobs: AscoraJobSummary[]; onSelect: (job: AscoraJobSummary) => void }) {
  return (
    <div className="screen">
      <div className="banner">
        <strong>SWMS.</strong> Select the Ascora job, choose the matching safe work method, confirm controls, then sign and attach the PDF.
      </div>
      {jobs.length === 0 && (
        <div className="empty"><strong>No open jobs found</strong><div>No Ascora jobs are assigned to you right now.</div></div>
      )}
      {jobs.map(job => (
        <button key={job.jobNumber} className="card card--accent" onClick={() => onSelect(job)} style={{ width: '100%', textAlign: 'left' }}>
          <div className="job-title">
            <div>
              <div className="job-title__number">{job.jobNumber}</div>
              <div className="job-title__name">{job.jobName || 'Untitled job'}</div>
            </div>
            <span className={`status ${job.swmsStatus === 'Completed' ? 'status--complete' : ''}`}>
              {job.swmsStatus === 'Completed' ? 'SWMS done' : 'Required'}
            </span>
          </div>
          <div style={{ marginTop: 10, color: '#6b6b6b', fontSize: 13 }}>
            {job.addressLine1}, {job.suburb} {job.postcode}
          </div>
          {job.swmsReference && (
            <div style={{ marginTop: 8, color: '#2e7d32', fontSize: 12, fontWeight: 750 }}>{job.swmsReference}</div>
          )}
        </button>
      ))}
    </div>
  );
}

function SwmsForm({
  job,
  templates,
  profile,
  onCancel,
  onSubmitted,
}: {
  job: AscoraJobSummary;
  templates: SwmsTemplate[];
  profile: M365Profile | null;
  onCancel: () => void;
  onSubmitted: (reference: string) => void;
}) {
  const [templateId, setTemplateId] = useState(templates[0]?.id || '');
  const template = useMemo(() => templates.find(t => t.id === templateId) || templates[0], [templates, templateId]);
  const [workActivity, setWorkActivity] = useState(template?.workActivity || '');
  const [responsiblePerson, setResponsiblePerson] = useState(profile?.displayName || '');
  const [responsiblePosition, setResponsiblePosition] = useState(profile?.positionTitle || '');
  const [responsibleContact, setResponsibleContact] = useState(profile?.mobilePhone || '');
  const [permits, setPermits] = useState<string[]>(template?.permits || []);
  const [licences, setLicences] = useState<string[]>(template?.licences || []);
  const [plantEquipment, setPlantEquipment] = useState<string[]>(template?.plantEquipment || []);
  const [hazardousMaterials, setHazardousMaterials] = useState<string[]>(template?.hazardousMaterials || []);
  const [ppe, setPpe] = useState<string[]>(template?.ppe || []);
  const [emergencyPlan, setEmergencyPlan] = useState(template?.emergencyPlan || '');
  const [steps, setSteps] = useState<SwmsStep[]>(template?.steps || []);
  const [workerRole, setWorkerRole] = useState(profile?.positionTitle || 'Plumber');
  const [licence, setLicence] = useState('');
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sigRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!template) return;
    setWorkActivity(template.workActivity);
    setPermits(template.permits);
    setLicences(template.licences);
    setPlantEquipment(template.plantEquipment);
    setHazardousMaterials(template.hazardousMaterials);
    setPpe(template.ppe);
    setEmergencyPlan(template.emergencyPlan);
    setSteps(template.steps);
  }, [templateId]);

  const submit = async () => {
    if (!template || !profile || !signature) return;
    setSubmitting(true);
    setError(null);
    const submission: SwmsSubmission = {
      jobId: job.jobId,
      jobNumber: job.jobNumber,
      templateId: template.id,
      templateTitle: template.title,
      date: formatDate(TODAY),
      reviewDate: formatDate(addYear(TODAY)),
      project: job.jobName || job.jobNumber,
      workLocation: `${job.addressLine1}, ${job.suburb} ${job.postcode}`,
      workActivity,
      responsiblePerson,
      responsiblePosition,
      responsibleContact,
      permits,
      licences,
      plantEquipment,
      hazardousMaterials,
      ppe,
      emergencyPlan,
      steps,
      workers: [{
        name: profile.displayName,
        role: workerRole,
        licence,
        employer: 'Harleys Complete Plumbing Services',
      }],
      signOff: {
        workerName: profile.displayName,
        workerEmail: profile.email,
        positionTitle: profile.positionTitle || workerRole,
        signedAt: new Date().toISOString(),
        signatureDataUrl: signature,
      },
    };
    try {
      const record = await submitSwms(submission);
      onSubmitted(record.swmsReference);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit SWMS');
    } finally {
      setSubmitting(false);
    }
  };

  if (!template) {
    return <div className="screen"><div className="empty"><strong>No templates configured</strong><div>Add SWMS templates before submitting.</div></div></div>;
  }

  return (
    <div className="screen">
      <div className="card card--accent">
        <div className="job-title">
          <div>
            <div className="job-title__number">{job.jobNumber}</div>
            <div className="job-title__name">{job.jobName}</div>
          </div>
        </div>
        <div style={{ marginTop: 10, color: '#6b6b6b', fontSize: 13 }}>{job.addressLine1}, {job.suburb} {job.postcode}</div>
      </div>

      <div className="section-title">Template</div>
      <div className="card">
        <div className="field">
          <label htmlFor="template">SWMS type</label>
          <select id="template" className="select" value={templateId} onChange={event => setTemplateId(event.target.value)}>
            {templates.map(t => <option key={t.id} value={t.id}>{t.code} · {t.title}</option>)}
          </select>
        </div>
        <TextField label="Work activity" value={workActivity} onChange={setWorkActivity} />
        <TextField label="Responsible person" value={responsiblePerson} onChange={setResponsiblePerson} />
        <div className="grid2">
          <TextField label="Position" value={responsiblePosition} onChange={setResponsiblePosition} />
          <TextField label="Contact" value={responsibleContact} onChange={setResponsibleContact} />
        </div>
      </div>

      <EditableList title="Permits / approvals" values={permits} onChange={setPermits} />
      <EditableList title="Licences / competency" values={licences} onChange={setLicences} />
      <EditableList title="Plant & equipment" values={plantEquipment} onChange={setPlantEquipment} />
      <EditableList title="Hazardous materials" values={hazardousMaterials} onChange={setHazardousMaterials} />
      <EditableList title="Mandatory PPE" values={ppe} onChange={setPpe} />

      <div className="section-title">Emergency Planning</div>
      <div className="card">
        <TextArea label="Emergency plan" value={emergencyPlan} onChange={setEmergencyPlan} />
      </div>

      <div className="section-title">Safe Work Method</div>
      {steps.map((step, index) => (
        <StepEditor
          key={`${step.step}-${index}`}
          step={step}
          index={index}
          onChange={next => setSteps(current => current.map((item, i) => i === index ? next : item))}
          onRemove={() => setSteps(current => current.filter((_, i) => i !== index))}
        />
      ))}
      <button className="button button--secondary" onClick={() => setSteps(current => [...current, {
        step: '',
        hazards: '',
        initialRisk: 'M',
        controls: '',
        residualRisk: 'L',
        actionBy: 'All Workers',
      }])}>
        Add SWMS Step
      </button>

      <div className="section-title">Consultation & Sign-Off</div>
      <div className="card">
        <div className="grid2">
          <TextField label="Worker role" value={workerRole} onChange={setWorkerRole} />
          <TextField label="Certificate / licence" value={licence} onChange={setLicence} />
        </div>
        <SignaturePad canvasRef={sigRef} onChange={setSignature} />
      </div>

      {error && <div className="banner">{error}</div>}
      <div className="actions">
        <button className="button" disabled={!profile || !signature || submitting} onClick={submit}>
          {submitting ? 'Submitting...' : 'Submit & attach to Ascora'}
        </button>
        <button className="button button--secondary" disabled={submitting} onClick={onCancel}>Back to jobs</button>
      </div>
    </div>
  );
}

function EditableList({ title, values, onChange }: { title: string; values: string[]; onChange: (values: string[]) => void }) {
  const [input, setInput] = useState('');
  return (
    <>
      <div className="section-title">{title}</div>
      <div className="card">
        <div className="chip-grid">
          {values.map(value => (
            <button key={value} className="chip chip--on" onClick={() => onChange(values.filter(item => item !== value))}>{value}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input className="input" value={input} onChange={event => setInput(event.target.value)} placeholder="Add item..." />
          <button className="button" style={{ width: 92 }} onClick={() => {
            const value = input.trim();
            if (!value) return;
            onChange([...values, value]);
            setInput('');
          }}>Add</button>
        </div>
      </div>
    </>
  );
}

function StepEditor({ step, index, onChange, onRemove }: { step: SwmsStep; index: number; onChange: (step: SwmsStep) => void; onRemove: () => void }) {
  return (
    <div className="card step-card">
      <div className="job-title" style={{ marginBottom: 10 }}>
        <strong>Step {index + 1}</strong>
        <button className="button button--secondary" style={{ width: 92, minHeight: 36 }} onClick={onRemove}>Remove</button>
      </div>
      <TextField label="Process step" value={step.step} onChange={value => onChange({ ...step, step: value })} />
      <TextArea label="Hazards / risks" value={step.hazards} onChange={value => onChange({ ...step, hazards: value })} />
      <div className="grid2">
        <RiskSelect label="Initial risk" value={step.initialRisk} onChange={value => onChange({ ...step, initialRisk: value })} />
        <RiskSelect label="Residual risk" value={step.residualRisk} onChange={value => onChange({ ...step, residualRisk: value })} />
      </div>
      <TextArea label="Controls" value={step.controls} onChange={value => onChange({ ...step, controls: value })} />
      <TextField label="Action by" value={step.actionBy} onChange={value => onChange({ ...step, actionBy: value })} />
    </div>
  );
}

function RiskSelect({ label, value, onChange }: { label: string; value: RiskCode; onChange: (value: RiskCode) => void }) {
  return (
    <div className="field">
      <label>{label}</label>
      <select className="select" value={value} onChange={event => onChange(event.target.value as RiskCode)}>
        {(['L', 'M', 'H', 'EH'] as RiskCode[]).map(risk => <option key={risk} value={risk}>{riskLabel(risk)}</option>)}
      </select>
    </div>
  );
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <div className="field"><label>{label}</label><input className="input" value={value} onChange={event => onChange(event.target.value)} /></div>;
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <div className="field"><label>{label}</label><textarea className="textarea" value={value} onChange={event => onChange(event.target.value)} /></div>;
}

function SignaturePad({ canvasRef, onChange }: { canvasRef: React.MutableRefObject<HTMLCanvasElement | null>; onChange: (value: string) => void }) {
  const drawing = useRef(false);

  const pos = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  return (
    <div className="field">
      <label>Signature</label>
      <canvas
        ref={canvasRef}
        width={580}
        height={160}
        style={{ width: '100%', height: 150, background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8, touchAction: 'none' }}
        onPointerDown={event => {
          drawing.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          const ctx = event.currentTarget.getContext('2d');
          if (!ctx) return;
          const p = pos(event);
          ctx.beginPath();
          ctx.moveTo(p.x * (event.currentTarget.width / event.currentTarget.clientWidth), p.y * (event.currentTarget.height / event.currentTarget.clientHeight));
        }}
        onPointerMove={event => {
          if (!drawing.current) return;
          const ctx = event.currentTarget.getContext('2d');
          if (!ctx) return;
          const p = pos(event);
          ctx.lineWidth = 3;
          ctx.lineCap = 'round';
          ctx.strokeStyle = '#231f20';
          ctx.lineTo(p.x * (event.currentTarget.width / event.currentTarget.clientWidth), p.y * (event.currentTarget.height / event.currentTarget.clientHeight));
          ctx.stroke();
        }}
        onPointerUp={event => {
          drawing.current = false;
          onChange(event.currentTarget.toDataURL('image/png'));
        }}
      />
      <button className="button button--secondary" style={{ marginTop: 8 }} onClick={() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          onChange('');
        }
      }}>Clear signature</button>
    </div>
  );
}

function DoneScreen({ job, reference, onBack }: { job: AscoraJobSummary; reference: string; onBack: () => void }) {
  return (
    <div className="screen">
      <div className="card card--accent">
        <div className="job-title">
          <div>
            <div className="job-title__number">{job.jobNumber}</div>
            <div className="job-title__name">SWMS submitted</div>
          </div>
          <span className="status status--complete">Attached</span>
        </div>
        <div style={{ marginTop: 12, fontWeight: 750 }}>{reference}</div>
        <div style={{ marginTop: 8, color: '#6b6b6b', fontSize: 13 }}>PDF attached to Ascora and logged for Take5 lookup.</div>
      </div>
      <button className="button" onClick={onBack}>Back to jobs</button>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
